import { ChipFilters, ResultsQuantity, SortBy } from '@/features/shared/ElasticComponent';
import ElasticComponentContext from '@/features/shared/ElasticComponent/ElasticComponentContext';
import {
  datalayerPush,
  getDataLayerNumFilters,
  getDataLayerSearchFilters,
  getDataLayerSearchFiltersObject,
} from '@/features/shared/HOCs/withGTM';
import { useIsRevision } from '@/features/shared/Hooks';
import { MuiTypography } from '@/features/shared/material-ui';
import { ColorNames, LoadingStatusValues, Theme } from '@/utils/constants';
import { resolveImageUrl } from '@/utils/resolvers';
import React, { useCallback } from 'react';
import TagManager from 'react-gtm-module';
import { BreadcrumbBox } from '../Breadcrumb';
import CMSBlock from './CMSBlock/CMSBlock';
import ResultsBlock from './ResultsBlock/ResultsBlock';
import SearchBar from './SearchBar/SearchBar';
import { Sidebar } from './Sidebar';
import { SolutionMarketplaceProps } from './SolutionMarketplace.interface';
import useStyles from './SolutionMarketplace.styles';
import { TabFilter } from './Tab';

const SolutionMarketplace = ({
  blocks,
  links,
  linksTitle,
  type = 'cms',
  contentButtons,
  labels,
  config,
  includeBreadcrumb,
  isCached,
  presentation = {},
}: Br.PropsWithContentButtons<SolutionMarketplaceProps>) => {
  const { queryParams, status, pagination } = React.useContext(ElasticComponentContext);
  const isRevision = useIsRevision();
  const [dataLayerId, setDataLayerId] = React.useState('');
  const classes = useStyles({});
  const genetecDarkLogoUrl = resolveImageUrl({ raw: config?.logo_genetec_dark });
  const genetecLightLogoUrl = resolveImageUrl({ raw: config?.logo_genetec_light });
  const containerRef = React.useRef<HTMLDivElement>(null);
  const variantName = (containerRef?.current?.closest('[data-datalayer-variant]') as HTMLDivElement)
    ?.dataset?.datalayerVariant;

  const renderLabel = useCallback(
    ({ className = '', genetecLogo = '' }: any) =>
      ({
        facet,
        item,
        fallback,
      }: {
        facet: string;
        fallback: any;
        item: { value: string; count: string };
      }) => {
        if (/is_genetec_certified/.test(facet)) {
          return (
            <span
              className={className}
              aria-label={labels?.partner_certified || 'Partner (Certified)'}
            >
              {labels?.partner_certified || 'Partner (Certified)'}{' '}
              {item.count ? `(${item.count})` : ''}
            </span>
          );
        }
        if (/developed_by/.test(facet)) {
          return `Genetec${item.count ? ` (${item.count})` : ''}`;
        }

        return fallback({ facet, item });
      },
    [labels?.partner_certified],
  );

  React.useEffect(() => {
    if (status !== LoadingStatusValues.loading) return;
    if (dataLayerId !== 'apply_filters') return;

    const filters = queryParams?.filters;
    const searchTerm = queryParams?.q?.trim();
    const searchFiltersObject = getDataLayerSearchFiltersObject(filters);
    const searchFilters = getDataLayerSearchFilters(filters);

    const dataLayer = {
      event: 'trackEvent-filterSearchResult',
      event_name: 'filter_search_results',
      ...(!!searchTerm && { search_term: searchTerm }),
      num_search_result: pagination?.totalResults || 0,
      index_num: String(pagination?.currentPage || 1),
      is_revision: isRevision,
      ...(!!searchFilters && { search_filters: searchFilters }),
      num_search_filters: getDataLayerNumFilters(filters),
      ...searchFiltersObject,
    };
    TagManager.dataLayer({ dataLayer });

    setDataLayerId('');
  }, [
    dataLayerId,
    isRevision,
    pagination?.currentPage,
    pagination?.totalResults,
    queryParams?.filters,
    queryParams?.q,
    status,
  ]);

  const handleApplyFilters = React.useCallback(() => {
    setDataLayerId('apply_filters');
  }, []);

  React.useEffect(() => {
    if (!queryParams?.q) return;

    Promise.resolve().then(() => {
      datalayerPush({
        compRef: containerRef,
        args: {
          event: 'searchQuery',
          revision: isRevision,
          query: queryParams?.q,
          variantName,
        },
        event: 'gtm:custom:search',
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="solution-marketplace--container" ref={containerRef}>
      {includeBreadcrumb && (
        <BreadcrumbBox className={classes.breadcrumb} data-datalayer-position="breadcrumb" />
      )}
      {/results/.test(type) && (
        <div className={classes.searchBar}>
          <MuiTypography variant="h2" className={classes.searchBarLabel}>
            {labels?.title ?? 'Solution Marketplace'}
          </MuiTypography>
          <div className={classes.searchBarInput}>
            <SearchBar labels={labels} />
          </div>
        </div>
      )}
      <div className={classes.content}>
        <div className={classes.sidebar} data-datalayer-position="sidebar">
          {contentButtons?.filter}
          <Sidebar
            links={links}
            linksTitle={linksTitle}
            renderLabel={renderLabel({
              className: classes.sidebarCertified,
              genetecLogo: genetecDarkLogoUrl,
            })}
            isSorted
            labels={labels}
            onApplyFiltersDataLayer={handleApplyFilters}
          />
        </div>
        <div className={classes.results}>
          {type === 'results' && (
            <div className={classes.heading}>
              <ResultsQuantity />
              {presentation?.isSortShown && <SortBy theme={Theme.light} />}
            </div>
          )}
          <div className={classes.tabFilter}>
            <TabFilter
              hasTitle={false}
              links={links}
              linksTitle={linksTitle}
              theme={Theme.dark}
              size="normal"
              spacing={2}
              labels={labels}
              renderLabel={renderLabel({
                className: classes.chipsCertified,
                genetecLogo: genetecDarkLogoUrl,
              })}
              onChange={handleApplyFilters}
            />
          </div>
          <div className={classes.chips}>
            <ChipFilters
              theme={Theme.dark}
              hoverable={false}
              chipColor={ColorNames.charcoal}
              renderLabel={renderLabel({
                className: classes.chipsCertified,
                genetecLogo: genetecLightLogoUrl,
              })}
            />
          </div>

          {type === 'cms' ? (
            blocks?.map((item, id) => (
              <CMSBlock
                // eslint-disable-next-line react/no-array-index-key
                key={id}
                listTitle={item.listTitle}
                listSuperheading={item.listSuperheading}
                contentList={item.contentList}
                labels={labels}
                config={config}
                cta={item.cta}
              >
                {contentButtons && contentButtons[`featuredContent${id + 1}`]}
              </CMSBlock>
            ))
          ) : (
            <ResultsBlock
              labels={labels}
              config={config}
              hasPagination
              isCached={isCached || false}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SolutionMarketplace;
